import { useCallback, useMemo } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useAcquisitionAwareUtm } from '@shared/hooks/useAcquisitionAwareUtm';
import type { UtmMedium } from '@shared/utils/utmParameters';
import { useHotelRoutePaths } from '@shared/utils/accommodations/hotelRoutes';
import type { AccommodationListingDTO, AccommodationsConsoleState } from '@shared/types/Accommodations.types';
import { RedirectMechanismType } from '@graphql/generated-base';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import {
  convertToJoyNativeCheckoutUrl,
  formatRoomOccupancyToString,
  JOY_HOTEL_DISCOUNT_CODE_PARAM,
  JOY_REFERRAL_CODE_PARAM,
  JOY_REFERRAL_REWARD_CODE_PARAM,
} from '@shared/utils/joyHotelUrl';
import { isValidBookingUrl } from '@shared/utils/accommodations';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import type { HotelSearchSource } from '@shared/constants/accommodations';
import { usePrioritizeTagParam } from '@shared/hooks/usePrioritizeTagParam';
import { useFormatRedirectURL } from '@shared/utils/formatting/redirectURL.formatter';

/**
 * Search parameters for accommodation redirect URLs
 */
type AccommodationSearchParams = Readonly<{
  rooms?: string;
  checkIn?: string;
  checkOut?: string;
  utm_source: string;
  utm_campaign: string;
  skipIfNoSearchParams?: string;
  utm_medium: UtmMedium;
  joyHotelDiscountCode?: string;
  skipAvailabilityCheck?: string;
}>;

export const createUtmParametersForAccommodationsConsolePage = (sourceName: 'guest_map' | 'admin_map' | 'ota'): AccommodationSearchParams => {
  return { utm_medium: 'web', utm_campaign: sourceName, utm_source: 'hotel_search' };
};

export const useAccommodationRedirectUrl = (state: AccommodationsConsoleState, sourceName: HotelSearchSource) => {
  const { eventHandle } = useEventInfo();
  const hotelRoute = useHotelRoutePaths();
  const { currency } = useTravelWhiteLabelCurrency();
  const formatRedirectURL = useFormatRedirectURL();
  const queryParams = useQueryParams();
  const rawDiscountCode = queryParams[JOY_HOTEL_DISCOUNT_CODE_PARAM];
  const discountCode = Array.isArray(rawDiscountCode) ? rawDiscountCode[0] : rawDiscountCode;
  // Only the native-checkout branch below needs these. Every other branch returns a URL from
  // `hotelRoute.*.buildRelativeUrl`, which retains both referral codes from the current URL on its own.
  const rawReferralRewardCode = queryParams[JOY_REFERRAL_REWARD_CODE_PARAM];
  const referralRewardCode = Array.isArray(rawReferralRewardCode) ? rawReferralRewardCode[0] : rawReferralRewardCode;
  const rawReferralCode = queryParams[JOY_REFERRAL_CODE_PARAM];
  const referralCode = Array.isArray(rawReferralCode) ? rawReferralCode[0] : rawReferralCode;
  // Same reason: the native URL is built outside `buildRelativeUrl`, whose retain hop carries the tag.
  const prioritizeTag = usePrioritizeTagParam();

  // Native checkout is built outside `buildRelativeUrl`, so resolve its UTM with the same shared hook.
  const internalUtm = useMemo(() => createUtmParametersForAccommodationsConsolePage(sourceName), [sourceName]);
  const resolvedUtm = useAcquisitionAwareUtm(internalUtm);
  const checkoutUtmParams = useMemo(
    () => ({
      utm_medium: resolvedUtm.utm_medium ?? internalUtm.utm_medium,
      utm_source: resolvedUtm.utm_source ?? internalUtm.utm_source,
      utm_campaign: resolvedUtm.utm_campaign ?? internalUtm.utm_campaign,
      ...(resolvedUtm.utm_source_secondary && { utm_source_secondary: resolvedUtm.utm_source_secondary }),
      ...(resolvedUtm.utm_content && { utm_content: resolvedUtm.utm_content }),
    }),
    [resolvedUtm, internalUtm],
  );

  return useCallback(
    (listing: AccommodationListingDTO, options?: { skipAvailabilityCheck?: boolean }) => {
      if (listing.redirectMechanisms) {
        const { hotelSearchPageUrl, hotelPageUrl } = extractHotelRedirects(listing.redirectMechanisms);
        const redirectData = hotelSearchPageUrl || hotelPageUrl;

        if (listing.isSponsored && redirectData?.type === RedirectMechanismType.custom) {
          /**
           * A paid placement ALWAYS keeps the affiliate hop, including on an event that has
           * `skipAffiliateRedirectWrapping` turned on.
           *
           * The hop is what carries the Partnerize camref, and the camref is what attributes the
           * click to the advertiser who paid for the slot. Honouring the event's opt-out here would
           * leave us showing inventory we were paid for and then throwing away the only evidence the
           * click happened.
           *
           * Scoped to the sponsored branch on purpose: every other listing on the same event —
           * organic results, room blocks, couple's picks — still respects the opt-out.
           */
          return formatRedirectURL({ url: redirectData.url.toString() }, { forceAffiliateWrapping: true });
        }

        if (redirectData?.type === RedirectMechanismType.joyNativeCheckout) {
          return convertToJoyNativeCheckoutUrl({
            currency,
            discountCode,
            referralRewardCode,
            referralCode,
            url: redirectData.url,
            checkIn: state.searchDates?.checkin,
            checkOut: state.searchDates?.checkout,
            roomsString: state.occupancies?.occupancies?.map(formatRoomOccupancyToString).join(','),
            utmParams: checkoutUtmParams,
            prioritizeTag,
          }).toString();
        }
      }

      const paramsData: AccommodationSearchParams = {
        // Raw internal defaults are fine here — `buildRelativeUrl` applies the inbound-wins merge.
        ...internalUtm,
        // Treatment: skip the sold-out interceptor on the redirect page and go straight to the hotel PDP.
        ...(options?.skipAvailabilityCheck && { skipAvailabilityCheck: 'true' }),
        ...(discountCode && { joyHotelDiscountCode: discountCode }),
        ...(state.searchDates && {
          checkIn: state.searchDates.checkin,
          checkOut: state.searchDates.checkout,
        }),
        ...(state.occupancies?.occupancies?.length && {
          rooms: state.occupancies.occupancies.map(formatRoomOccupancyToString).join(','),
        }),
        ...(state.searchDates &&
          state.occupancies?.occupancies?.length && {
            skipIfNoSearchParams: 'true',
          }),
      };

      /**
       * Event-less console — today that is only the standalone honeymoon product at `/honeymoon`,
       * the one surface that renders this console outside an event.
       *
       * Every branch below builds `/:eventHandle/...`, so without a handle this used to return
       * undefined and leave every card unclickable: `OtaListingEntry` only becomes an anchor when a
       * redirect URL exists. Room blocks and couple's picks cannot appear here — both are event
       * inventory, and the honeymoon scenario skips couple's picks outright — so the property page
       * is the only destination there is.
       */
      if (!eventHandle) {
        if (!listing.joyPlaceId) {
          return undefined;
        }
        /**
         * Dates go out lowercase here, unlike every event route above.
         *
         * The event routes land on the redirect interceptor, which reads dates through
         * `useCheckInCheckoutDates` — that hook reads `checkIn`/`checkOut`. This link lands on the
         * PDP itself, whose `SearchParamsObserver` reads `checkin`/`checkout` (or
         * `startDate`/`endDate`) and nothing else. Sending only the camelCase pair meant the PDP
         * found no dates and fell back to `calculateStayDates(undefined)` — a stay one month from
         * today — so the guest was quoted availability for dates they never searched.
         *
         * `retainSupportedStaysParams` does not rescue this: it carries the discount and referral
         * codes only, never dates.
         */
        return hotelRoute.honeymoonHotelDetails.buildRelativeUrl(listing.joyPlaceId, {
          ...paramsData,
          ...(state.searchDates && { checkin: state.searchDates.checkin, checkout: state.searchDates.checkout }),
        });
      }

      if (listing.type === 'roomBlock') {
        if (!listing.id || !listing.bookingUrl) {
          return undefined;
        }
        if (!isValidBookingUrl(listing.bookingUrl)) {
          return undefined;
        }
        return hotelRoute.roomblock.buildRelativeUrl(eventHandle, listing.id, paramsData);
      }

      if (listing.isPickedByCouple) {
        if (!listing.id) {
          return undefined;
        }
        return hotelRoute.accommodationPlace.buildRelativeUrl(eventHandle, listing.id, paramsData);
      }

      if (!listing.joyPlaceId) {
        return undefined;
      }

      return hotelRoute.hotelDetails.buildRelativeUrl(eventHandle, listing.joyPlaceId, paramsData);
    },
    [
      eventHandle,
      internalUtm,
      checkoutUtmParams,
      discountCode,
      referralRewardCode,
      referralCode,
      prioritizeTag,
      state.searchDates,
      state.occupancies.occupancies,
      hotelRoute.hotelDetails,
      hotelRoute.honeymoonHotelDetails,
      hotelRoute.roomblock,
      hotelRoute.accommodationPlace,
      currency,
      formatRedirectURL,
    ],
  );
};
